import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { formatCurrency } from '@/lib/formatters';

import { FactorBar } from './factor-bar';
import type { ScoreFactor } from './factor-bar';
import { ScoreGauge } from './score-gauge';

const ESTIMATED_VALUE_EXPLANATION =
  'Estimated Value — The projected commission if this opportunity converts, calculated as: ' +
  'Base Amount × Commission Rate × Confidence. ' +
  'Base Amount: premium and/or account value, whichever is ≥ $10k (averaged if both qualify). ' +
  'Commission Rate: set by product type (e.g. ~5.5% FIA, ~5% RILA, ~4.5% VA, ~3% MYGA). ' +
  'Confidence: scaled by the propensity score — higher scores count more of the value, lower scores discount it. ' +
  'Values under $500 show as $0; all estimates are capped at $100,000.';

interface ScoreBreakdownProps {
  score: number;
  factors: ScoreFactor[];
  estimatedValue?: number | null;
}

export function ScoreBreakdown({ score, factors, estimatedValue }: ScoreBreakdownProps) {
  const sortedFactors = [...factors].sort((a, b) => b.weight - a.weight);
  const midpoint = Math.ceil(sortedFactors.length / 2);
  const leftFactors = sortedFactors.slice(0, midpoint);
  const rightFactors = sortedFactors.slice(midpoint);
  const scoreLabel =
    score >= 80 ? 'High Propensity' : score >= 50 ? 'Medium Propensity' : 'Low Propensity';

  return (
    <Card style={{ boxShadow: 'var(--inset-shadow-deep)' }}>
      <CardHeader className="pb-2">
        <CardTitle>Propensity Score</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-6 xl:grid-cols-[minmax(220px,0.55fr)_minmax(0,1.15fr)_minmax(0,1.15fr)] xl:gap-10">
          <div className="flex items-center justify-start gap-5">
            <ScoreGauge score={score} size="md" showLabel={false} />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-success">{scoreLabel}</p>
              <div
                className="my-3 w-20 border-t"
                style={{ borderColor: 'hsl(var(--muted-foreground) / 0.45)' }}
              />
              <div>
                <div className="flex items-center gap-1">
                  <p className="text-xs font-medium text-muted-foreground">Estimated Value</p>
                  <InfoTooltip
                    text={ESTIMATED_VALUE_EXPLANATION}
                    label={`Estimated value details: ${ESTIMATED_VALUE_EXPLANATION}`}
                  />
                </div>
                <p className="mt-0.5 text-2xl font-semibold font-tabular">
                  {formatCurrency(estimatedValue)}
                </p>
              </div>
            </div>
          </div>

          <div className="min-w-0">
            {leftFactors.length > 0 ? (
              <div>
                {leftFactors.map((factor, index) => (
                  <FactorBar key={index} factor={factor} />
                ))}
              </div>
            ) : (
              <p className="text-sm italic text-muted-foreground">No factor breakdown available</p>
            )}
          </div>

          <div className="min-w-0">
            {rightFactors.map((factor, index) => (
              <FactorBar key={index} factor={factor} />
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
